import React, { useState } from 'react';
import styled from 'styled-components';
import ArrowIcon from './icons/arrow.svg';
import { Link } from 'react-router-dom';
import { useDarkMode } from 'context/DarkModeContext';

const SubMenuDiv = styled.ul`
  width: 260px;  
  list-style: none;
  position: absolute;
  margin: 0;
  padding: 0;
  top: 0;
  right: ${(props) => (props.right ? '-238px' : '-239px')};  /* adjusted to match new width */
  visibility: hidden;
  opacity: 0;
  z-index: 150;
  border-top: 1px solid var(--admincat-color-grey-1);
  border-right: 1px solid var(--admincat-color-grey-1);
  border-bottom: 1px solid var(--admincat-color-grey-1);

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
    max-height: ${({ $isOpen }) => ($isOpen ? '2000px' : '0')};
    overflow: hidden;
    border: none;
    border-left: 3px solid var(--admincat-color-grey-0);
    margin-left: 15px;
    margin-top: ${({ $isOpen }) => ($isOpen ? '5px' : '0')};
    margin-bottom: ${({ $isOpen }) => ($isOpen ? '5px' : '0')};
    padding-left: 10px;
    transition: all 300ms ease;
  }
`;

const SubMenuList = styled.li`
  display: block;
  position: relative;
  padding: 10px;
  background: var(--admincat-color-grey-1);
  transition: 300ms;
  list-style: none;

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: var(--admincat-color-grey-3);
    width: 100%;
  }

  &:hover {
    cursor: pointer;
    background: var(--admincat-color-grey-0);
  }

  &:hover > ul {
    visibility: visible;
    opacity: 1;
  }

  .title {
    color: var(--admincat-color-grey-3);
    flex: 1;
  }
  
  .icon {
    margin-right: 10px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  @media (max-width: 768px) {
    padding: 12px 8px;
    margin-bottom: 2px;
    
    .title {
      font-size: 13px;
      line-height: 1.4;
      word-wrap: break-word;
      padding-right: 30px;
    }

    .icon {
      width: 18px;
      height: 18px;
      margin-right: 8px;
    }
  }
`;

const MenuItemWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
`;

const ArrowDiv = styled.div`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    display: ${({ $hasSubNav }) => ($hasSubNav ? 'flex' : 'none')};
    transform: ${({ $isOpen }) => 
      $isOpen ? 'translateY(-50%) rotate(90deg)' : 'translateY(-50%)'};
    transition: transform 300ms ease;
    right: 8px;

    img {
      width: 14px;
      height: 14px;
    }
  }
`;

const SubMenu = ({ sidebar, background, setWidth, right, items, tr, color, closeSidebar, $isOpen }) => {
  const [openSubMenus, setOpenSubMenus] = useState({});
  const { isDarkMode } = useDarkMode();

  const toggleSubMenu = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.innerWidth <= 768) {
      setOpenSubMenus(prev => ({
        ...prev,
        [index]: !prev[index]
      }));
    }
  };

  const handleLinkClick = (hasSubNav, e) => {
    if (window.innerWidth <= 768) {
      if (hasSubNav) {
        e.preventDefault();
      } else {
        closeSidebar();
      }
    }
  };

  return (
    <SubMenuDiv
      background={background}
      setWidth={setWidth}
      sidebar={sidebar}
      right={right}
      $isOpen={$isOpen}
    >
      {items.map((item, index) => (
        <SubMenuList key={item.path || `item-${index}`}>
          {item.subNav ? (
            <MenuItemWrapper onClick={(e) => window.innerWidth <= 768 && toggleSubMenu(index, e)}>
              <Link 
                to={item.path} 
                onClick={(e) => handleLinkClick(true, e)}
              >
                <div className="icon">
                  <img src={isDarkMode ? item.iconDark : item.icon} alt={item.title} />
                </div>
                <div className="title">{tr(item.title)}</div>
              </Link>
              <ArrowDiv 
                $hasSubNav={true} 
                $isOpen={openSubMenus[index]}
              >
                <img src={ArrowIcon} alt="expand" />
              </ArrowDiv>
            </MenuItemWrapper>
          ) : (
            <Link 
              to={item.path} 
              onClick={(e) => handleLinkClick(false, e)}
            >
              <div className="icon">
                <img src={isDarkMode ? item.iconDark : item.icon} alt={item.title} />
              </div>
              <div className="title">{tr(item.title)}</div>
            </Link>
          )}
          
          {item.subNav && (
            <SubMenuDiv
              background={background}
              setWidth={setWidth}
              sidebar={sidebar}
              right={right}
              color={color}
              $isOpen={openSubMenus[index]}
            >
              {item.subNav.map((subItem, subIndex) => (
                <SubMenuList key={subItem.path || `subitem-${index}-${subIndex}`}>
                  <Link 
                    to={subItem.path} 
                    onClick={() => window.innerWidth <= 768 && closeSidebar()}
                  >
                    <div className="icon">
                      <img src={isDarkMode ? subItem.iconDark : subItem.icon} alt={subItem.title} />
                    </div>
                    <div className="title">{tr(subItem.title)}</div>
                  </Link>
                </SubMenuList>
              ))}
            </SubMenuDiv>
          )}
        </SubMenuList>
      ))}
    </SubMenuDiv>
  );
};

export default SubMenu;